// Shared client-side feature-flag fetch. Consolidates the byte-identical
// (modulo comment/whitespace formatting) `getFeatures()` copies formerly
// duplicated in apps/library/src/api.js and apps/store/src/api.js.
//
// Fail-OPEN by design: this endpoint gates non-destructive feature
// availability, so a fetch failure (network error, non-2xx response) must
// never brick the app — it returns `{}` and callers that treat a missing
// key as "assume enabled" keep working offline/degraded. Call sites that
// need fail-CLOSED behavior for a destructive feature do that themselves
// by checking `flag === true` (an empty `{}` makes `{}.flag === true`
// false, which is exactly the fail-closed behavior those call sites rely
// on) — this module does not and must not change that truthiness contract.
//
// NOT a cache: every call issues a fresh fetch, matching both original
// copies (neither memoized).
//
// Deliberately NOT consolidated: src/client/module-registry.js's
// singleton `moduleRegistry` has its own `load()`/`getFeatures()` pair
// with a different shape (stateful cache + synchronous getter +
// localStorage override layer) and its fetch omits `credentials: 'include'`
// (relies on default same-origin credentials) — a real divergence from
// this helper, not just formatting, so it is left untouched rather than
// silently folded in.
//
// Browser-safe (no DOM access at module top level) per src/shared/ convention.

/**
 * Fetch feature flags from /api/admin/features. Returns the parsed JSON
 * body on a 2xx response, or `{}` on any non-2xx response or thrown
 * exception (offline, dev, server error) — screens are expected to treat
 * a missing key as "assume enabled" unless they explicitly check
 * `=== true` for a fail-closed gate.
 */
export async function getFeatures() {
  try {
    const res = await fetch('/api/admin/features', { credentials: 'include' });
    if (!res.ok) return {};
    return res.json();
  } catch {
    return {};
  }
}
